import { useFormik } from 'formik'
import * as Yup from 'yup'

import InfoCircleOutlinedIcon from '@ant-design/icons/InfoCircleOutlined'

import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import DialogTitle from '@components/_mui/DialogTitle'

import { Lookup, useLookup } from '@shared/providers/src/DropdownOptionsProvider'
import { AppointmentStatus } from '@shared/utils'

import { usePatientLabs } from '@pages/Labs/PatientLabs'
import { LIMIT } from '@pages/Patients/Patient/ProfileTab/Labs'
import InputControl from '@components/InputControl/InputControl'
import LinearProgress from '@components/LinearProgress'

import { useUpcomingAppointments } from './Menu.hooks'

const testId = 'change-status-dialog'

/**
 * Display a dialog confirmation for changing a patient's status
 */
export default function ChangeStatusDialog({ patientId, open = false, title, onClose, onConfirm }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: {
          'data-testid': testId,
        },
      }}
    >
      <Form patientId={patientId} title={title} onConfirm={onConfirm} onClose={onClose} />
    </Dialog>
  )
}

function Form({ patientId, title, onConfirm, onClose }) {
  const reasons = useLookup(Lookup.StatusChangingReasons)

  const { data: appointments, isPending: isAppointmentsPending } = useUpcomingAppointments()
  const { data: labs, isPending: isLabsPending } = usePatientLabs(
    patientId,
    { filter: 'follow-up', limit: LIMIT },
    {
      enabled: Boolean(patientId),
    }
  )

  const hasUpcomingAppointments = Boolean(appointments?.some((appointment) => appointment.status === AppointmentStatus.Scheduled))
  const hasOngoingAppointments = Boolean(
    appointments?.some((appointment) => [AppointmentStatus.Waiting, AppointmentStatus.InProgress].includes(appointment.status))
  )
  const hasDocumentingAppointments = Boolean(appointments?.some((appointment) => appointment.status === AppointmentStatus.Documenting))
  const hasFollowUpLabs = labs?.length > 0

  const formik = useFormik({
    initialValues: {
      reason: '',
      details: '',
    },
    validationSchema: Yup.object().shape({
      reason: Yup.string().required('Reason is required'),
      details: Yup.string()
        .trim()
        .when('reason', {
          is: (reason) => reason === 'No longer interested',
          then: Yup.string().trim().required('Details are required'),
        }),
    }),
    onSubmit: () => onConfirm(formik.values),
  })

  const isLoading = isAppointmentsPending || isLabsPending
  const hasWarnings = hasUpcomingAppointments || hasOngoingAppointments || hasDocumentingAppointments || hasFollowUpLabs

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <DialogTitle>{title}</DialogTitle>
      <LinearProgress loading={isLoading} />
      <DialogContent>
        <Stack spacing={2}>
          <Collapse in={hasWarnings} unmountOnExit>
            <Alert
              color="warning"
              variant="border"
              icon={<InfoCircleOutlinedIcon />}
              sx={{ '& .MuiAlert-icon': { alignItems: 'flex-start' } }}
            >
              <AlertTitle>Review before changing status</AlertTitle>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {hasUpcomingAppointments && (
                  <Typography component="li" variant="body2">
                    Scheduled appointments will be canceled
                  </Typography>
                )}
                {hasOngoingAppointments && (
                  <Typography component="li" variant="body2">
                    A visit is waiting or in progress
                  </Typography>
                )}
                {hasDocumentingAppointments && (
                  <Typography component="li" variant="body2">
                    Some appointments are still being documented
                  </Typography>
                )}
                {hasFollowUpLabs && (
                  <Typography component="li" variant="body2">
                    There are follow-up labs to review
                  </Typography>
                )}
              </Box>
            </Alert>
          </Collapse>
          <InputControl field="reason" formikProps={formik}>
            <TextField label="Reason for changing status" disabled={isLoading} required fullWidth select>
              {reasons?.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </TextField>
          </InputControl>
          <InputControl field="details" formikProps={formik}>
            <TextField required fullWidth select disabled={formik.values.reason !== 'No longer interested' || isLoading}>
              <MenuItem value="Desires / requires injectable">Desires / requires injectable</MenuItem>
              <MenuItem value="Local care provider">Local care provider</MenuItem>
              <MenuItem value="Monogamous relationship">Monogamous relationship</MenuItem>
              <MenuItem value="Not sexually active">Not sexually active</MenuItem>
              <MenuItem value="Other telePrEP provider">Other telePrEP provider</MenuItem>
              <MenuItem value="Patient dissatisfied">Patient dissatisfied</MenuItem>
            </TextField>
          </InputControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error" variant="text" data-testid={`${testId}-reject`} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          loading={formik.isSubmitting}
          disabled={!formik.dirty || isLoading}
          type="submit"
          variant="contained"
          data-testid={`${testId}-confirm`}
          autoFocus
        >
          Confirm
        </Button>
      </DialogActions>
    </form>
  )
}
